import { svgIconPaths } from '@/assets/svg/icons';
import CustomButton from '@/components/atoms/CustomButton';
import TabButtons from '@/components/atoms/TabButtons';
import CreateNotificationModal from '@/components/molecules/CreateNotificationModal';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import appRoutes from '@/constants/routes/app';
import { ORG_DISABLED_EXPLANATION } from '@/constants/stringVars';
import useSendNotificationToOrganizations from '@/hooks/notifications/useSendNotificationToOrganizations';
import { useOrganizationById } from '@/hooks/organization/organization';
import OrganizationContractsTab from '@/modules/organization/tabs/contracts';
import OrganizationCoordinatorsTab from '@/modules/organization/tabs/Coordinators';
import OrganizationDataTab from '@/modules/organization/tabs/Data';
import DeactivationTab from '@/modules/organization/tabs/Deactivation';
import OrganizationInfoTab from '@/modules/organization/tabs/Info';
import OrganizationRosterTab from '@/modules/organization/tabs/Roster';
import colors from '@/theme/colors';
import { TNotificationForm } from '@/validation/notification';
import { HStack, useDisclosure, VStack } from '@chakra-ui/react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

type TAB_OPTIONS = 'info' | 'data' | 'roster' | 'coordinators' | 'contracts' | 'deactivation';

const OrganizationPage = () => {
  const { organizationId } = Route.useParams();
  const navigate = useNavigate();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const [activeTab, setActiveTab] = useState<TAB_OPTIONS>('info');

  const { isLoading, data: organizationData } = useOrganizationById(organizationId);

  const { isPending, mutate } = useSendNotificationToOrganizations([organizationId], () => {
    onClose();
  });

  const onSubmit = (form: TNotificationForm) => {
    mutate(form);
  };

  const onTabClick = (tab: TAB_OPTIONS) => {
    setActiveTab(tab);
  };

  return (
    <SidebarItemViewTemplate
      title={'Organization'}
      buttonLabel="Send Notification"
      onButtonClick={onOpen}
      isButtonDisabled={!organizationData?.isActive}
      buttonDisabledExplanation={ORG_DISABLED_EXPLANATION}
    >
      <HStack justify={'flex-start'} width={'100%'} marginTop={'20px'}>
        <CustomButton
          isTransparent
          iconBeforeLabelSvgPath={svgIconPaths.leftArrow}
          labelColor={colors.background.blueGray}
          labelHoverColor="extra.black"
          label="Back to Organizations"
          hoverBackgroundColor="transparent"
          borderHoverColor="transparent"
          onClick={() => navigate({ to: appRoutes.organizations })}
          labelStyle={{ marginBottom: '5px', fontSize: '16px' }}
        />
      </HStack>
      <VStack flex={1} width={'100%'}>
        <TabButtons
          options={[
            {
              onClick: () => onTabClick('info'),
              labelValue: 'info',
              labelDisplay: 'Info',
            },
            {
              onClick: () => onTabClick('data'),
              labelValue: 'data',
              labelDisplay: 'Data',
            },
            {
              onClick: () => onTabClick('roster'),
              labelValue: 'roster',
              labelDisplay: 'Roster',
            },
            {
              onClick: () => onTabClick('coordinators'),
              labelValue: 'coordinators',
              labelDisplay: 'Coordinators',
            },
            {
              onClick: () => onTabClick('contracts'),
              labelValue: 'contracts',
              labelDisplay: 'Contracts',
            },
            {
              onClick: () => onTabClick('deactivation'),
              labelValue: 'deactivation',
              labelDisplay: organizationData?.isActive ? 'Deactivation' : 'Reactivation',
            },
          ]}
          selectedOptionValue={activeTab}
        />
        <>
          {activeTab === 'info' && <OrganizationInfoTab isLoading={isLoading} organizationData={organizationData} />}
          {activeTab === 'data' && <OrganizationDataTab isLoading={isLoading} organizationData={organizationData} />}
          {activeTab === 'roster' && (
            <OrganizationRosterTab organizationId={organizationId} isOrgActive={organizationData?.isActive} />
          )}
          {activeTab === 'coordinators' && (
            <OrganizationCoordinatorsTab organizationId={organizationId} isOrgActive={organizationData?.isActive} />
          )}
          {activeTab === 'contracts' && <OrganizationContractsTab organizationId={organizationId} />}
          {activeTab === 'deactivation' && (
            <DeactivationTab isLoading={isLoading} organizationData={organizationData} />
          )}
        </>
      </VStack>
      <CreateNotificationModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onSubmit}
        isLoading={isPending}
        title="Send Notification to Organization"
      />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/organizations/$organizationId')({
  component: OrganizationPage,
});
